import { EditNote, PriorityHigh } from "@mui/icons-material";
import Check from "@mui/icons-material/Check";
import Circle from "@mui/icons-material/Circle";
import DoNotDisturb from "@mui/icons-material/DoNotDisturb";
import { styled, Box, SxProps } from "@mui/material";
import _ from "lodash";
import { DateTime } from "luxon";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";

import {
  User,
  Campaign,
  GetThreadQuery,
  Tag,
} from "~frontend/__generated__/graphql";
import TagIcon from "~frontend/components/icons/Tag";
import { palette } from "~frontend/theme";

import NeedsResponseBullet from "../NeedsResponseBullet";

const iconWithColor = (color?: string): SxProps => ({
  width: "12px",
  height: "12px",
  color,
});

function ActionMessage({
  action,
}: {
  action: GetThreadQuery["getThread"]["threadActions"][number];
}): ReactElement {
  const userFullName = fullName(action.performedByUser);
  const dateTime = DateTime.fromMillis(action.createdAt).toLocaleString(
    DateTime.DATETIME_SHORT
  );

  switch (action.__typename) {
    case "OptedOutThreadAction":
      // Automatic opt-outs are distinguished from manual opt-outs by not being
      // performed by a user.
      if (!action.performedByUser) {
        return (
          <>
            <DoNotDisturb sx={iconWithColor(palette.darkGray)} />
            <FormattedMessage
              defaultMessage="Automatically opted out at {dateTime}"
              description="Action to mark a contact as opted out"
              values={{ dateTime }}
            />
          </>
        );
      }
      return (
        <>
          <DoNotDisturb sx={iconWithColor(palette.darkGray)} />
          <FormattedMessage
            defaultMessage="Marked opted out by {userFullName} at {dateTime}"
            description="Action to mark a contact as opted out"
            values={{ userFullName, dateTime }}
          />
        </>
      );
    case "OptedInThreadAction":
      return (
        <>
          <Check sx={iconWithColor(palette.darkGray)} />
          <FormattedMessage
            defaultMessage="Marked opted in by {userFullName} at {dateTime}"
            description="Action to mark a contact as opted in"
            values={{ userFullName, dateTime }}
          />
        </>
      );
    case "MarkedNeedsResponseThreadAction":
      return (
        <>
          <NeedsResponseBullet sx={iconWithColor(palette.darkGray)} />
          <FormattedMessage
            defaultMessage="Marked as needing a response by {userFullName} at {dateTime}"
            description="Action to mark a contact as needing a response"
            values={{ userFullName, dateTime }}
          />
        </>
      );
    case "MarkedNotNeedsResponseThreadAction":
      return (
        <>
          <Circle sx={iconWithColor(palette.lightGray)} />
          <FormattedMessage
            defaultMessage="Marked as not needing a response by {userFullName} at {dateTime}"
            description="Action to mark a contact as not needing a response"
            values={{ userFullName, dateTime }}
          />
        </>
      );
    case "ApplyCampaignTagsThreadAction":
      return (
        <>
          <TagIcon sx={iconWithColor(palette.darkGray)} />
          <ApplyTagsMessage
            user={action.performedByUser}
            campaign={action.campaign}
            tags={action.actionData.tags}
          />
        </>
      );
    case "RemoveCampaignTagsThreadAction":
      return (
        <>
          <TagIcon sx={iconWithColor(palette.darkGray)} />
          <FormattedMessage
            defaultMessage={`{userFullName} removed {numTags, plural,
                =1 {tag}
                other {tags}
              } {tagNames} for campaign "{campaignName}"`}
            description="Action to remove tags from a contact connected to a campaign"
            values={{
              userFullName,
              numTags: action.actionData.tags.length,
              tagNames: action.actionData.tags
                .map(tag => `"${tag.name}"`)
                .join(", "),
              // remove actions are never created without a campaign
              campaignName: action.campaign!.name,
            }}
          />
        </>
      );
    case "EditCampaignContactNoteThreadAction":
      return (
        <>
          <EditNote sx={iconWithColor(palette.darkGray)} />
          {action.campaign ? (
            <FormattedMessage
              defaultMessage={`{userFullName} edited contact note for campaign "{campaignName}"`}
              description="Action to edit the note for a contact connected to a campaign"
              values={{ userFullName, campaignName: action.campaign.name }}
            />
          ) : (
            <FormattedMessage
              defaultMessage={`{userFullName} edited contact note`}
              description="Action to edit the note for a contact with an unknown campaign"
              values={{ userFullName }}
            />
          )}
        </>
      );
    case "EscalateThreadAction":
      return (
        <>
          <PriorityHigh sx={iconWithColor(palette.darkGray)} />
          <FormattedMessage
            defaultMessage="{userFullName} escalated to {escalationName}"
            description="Action to escalate a thread"
            values={{
              userFullName,
              escalationName: action.actionData.escalationInbox.name!,
            }}
          />
        </>
      );
    case "DeescalateThreadAction":
      return (
        <>
          <PriorityHigh sx={iconWithColor(palette.darkGray)} />
          <FormattedMessage
            defaultMessage="{userFullName} de-escalated from {escalationName}"
            description="Action to de-escalate a thread"
            values={{
              userFullName,
              escalationName: action.actionData.escalationInbox.name!,
            }}
          />
        </>
      );
    case "MoveEscalationThreadAction":
      return (
        <>
          <PriorityHigh sx={iconWithColor(palette.darkGray)} />
          <FormattedMessage
            defaultMessage="{userFullName} changed escalation from {fromEscalationName} to {toEscalationName, select, null {an unknown escalation} other {{toEscalationName}}}"
            description="Action to move a thread between escalation inboxes"
            values={{
              userFullName,
              fromEscalationName: action.actionData.fromEscalationInbox.name!,
              toEscalationName:
                action.actionData.toEscalationInbox?.name ?? "null",
            }}
          />
        </>
      );
    case "MoveInboxThreadAction":
      return (
        <>
          <PriorityHigh sx={iconWithColor(palette.darkGray)} />
          <FormattedMessage
            defaultMessage="{userFullName} moved from {fromInboxName} to {toInboxName}"
            description="Action to move a thread between inboxes"
            values={{
              userFullName,
              fromInboxName: action.actionData.fromInbox?.name ?? "no inbox",
              toInboxName: action.actionData.toInbox.name!,
            }}
          />
        </>
      );

    default:
      throw new Error(`Unknown thread action type ${action.__typename}`);
  }
}

const fullName = (user?: { fullName: string } | null) =>
  user?.fullName || "an unknown user";

const ApplyTagsMessage = ({
  tags,
  user,
  campaign,
}: {
  tags: Pick<Tag, "name" | "id">[];
  user?: Pick<User, "fullName"> | null;
  campaign?: Pick<Campaign, "name"> | null;
}) => {
  const sharedValues = {
    numTags: tags.length,
    tagNames: tags.map(tag => `"${tag.name}"`).join(", "),
  };
  const userFullName = fullName(user);
  if (campaign) {
    const campaignName = campaign.name;
    if (user) {
      return (
        <FormattedMessage
          defaultMessage={`{userFullName} applied {numTags, plural,
              =1 {tag}
              other {tags}
            } {tagNames} for campaign "{campaignName}"`}
          description="Action to add tags to a contact connected to a campaign"
          values={{ ...sharedValues, userFullName, campaignName }}
        />
      );
    } else {
      return (
        <FormattedMessage
          defaultMessage={`{numTags, plural,
              =1 {Tag}
              other {Tags}
            } {tagNames} automatically applied for campaign "{campaignName}"`}
          description="Automatic action to add tags to a contact connected to a campaign"
          values={{ ...sharedValues, campaignName }}
        />
      );
    }
  } else {
    if (user) {
      return (
        <FormattedMessage
          defaultMessage={`{userFullName} applied {numTags, plural,
              =1 {tag}
              other {tags}
            } {tagNames}`}
          description="Action to add tags to a contact with an unknown campaign"
          values={{ ...sharedValues, userFullName }}
        />
      );
    } else {
      return (
        <FormattedMessage
          defaultMessage={`{numTags, plural,
              =1 {Tag}
              other {Tags}
            } {tagNames} automatically applied to a contact with an unknown campaign"`}
          description="Automatic action to add tags to a contact with an unknown campaign"
          values={{ ...sharedValues }}
        />
      );
    }
  }
};

const ActionWrapper = styled(Box)({
  fontStyle: "italic",
  color: palette.darkGray,
});

export default function ThreadActionComponent({
  action,
}: {
  action: GetThreadQuery["getThread"]["threadActions"][0];
}): ReactElement {
  return (
    <ActionWrapper>
      <ActionMessage action={action} />
    </ActionWrapper>
  );
}
